import plurCount from "@-/com.npmjs.plur-count";
import { useQuery } from "@-/com.npmjs.tanstack__react-query";
import { captureAnalyticsEvent } from "@-/dev.improve.haniverse.lib.analytics";
import { useDeviceAccount, useDeviceSetting } from "@-/dev.improve.haniverse.lib.device-settings";
import { reviewFocusLabel } from "@-/dev.improve.haniverse.lib.learn.review-focus";
import { useIsSyncingFromCloud } from "@-/dev.improve.haniverse.lib.sync-push.signal";
import { useIsPremium } from "@-/dev.improve.haniverse.components.superwall";
import { useLearnDeckQueueHeadNavigator } from "@-/dev.improve.haniverse.routes.nav.app.learn.deck.navigator";
import {
  type LearnSummaryPageResolution,
  useLearnSummaryPageQueryOptions,
} from "@-/dev.improve.haniverse.routes.nav.app.learn.summary.navigator";
import { useCallback } from "react";
import { useStartLearnDeckFromSummary } from "#use-start-learn-deck-from-summary";

export type LearnSummaryVariant =
  | "continue_learning"
  | "daily_limit"
  | "free_tier_complete"
  | "nothing_to_learn"
  | "own_daily_limit"
  | "review_focus_empty"
  | "start_reviewing"
  | "welcome";

/** Which summary state the learner sees; the one place the render branches are decided. */
export function learnSummaryVariant({
  isPremium,
  resolution,
  reviewFocus,
}: {
  isPremium: boolean;
  resolution: LearnSummaryPageResolution;
  reviewFocus: string;
}): LearnSummaryVariant {
  if (resolution.isNewUser) {
    return "welcome";
  }
  if (
    resolution.dueReviewCount > 0 &&
    (resolution.hasReviewCandidate ?? resolution.reviewDrill !== null)
  ) {
    return "start_reviewing";
  }
  if (resolution.continueDestination !== null) {
    return "continue_learning";
  }
  if (reviewFocus !== "all") {
    return "review_focus_empty";
  }
  // `daily_limit` is the free tier's cap (it offers the upgrade); `own_daily_limit` is the
  // learner's own Daily New Cards setting.
  const dailyLimit = resolution.dailyNewGraphemeLimitReached;
  if (dailyLimit !== null) {
    return dailyLimit.isFreeTierCap ? "daily_limit" : "own_daily_limit";
  }
  return isPremium ? "nothing_to_learn" : "free_tier_complete";
}

/** `"N reviews due"` (or `"N <focus> reviews due"`), or `null` when nothing is due. */
export function learnSummaryDueReviewLabel(
  dueReviewCount: number,
  reviewFocus: Parameters<typeof reviewFocusLabel>[0],
): string | null {
  if (dueReviewCount === 0) {
    return null;
  }
  return reviewFocus === "all"
    ? `${plurCount(dueReviewCount, "review")} due`
    : `${dueReviewCount} ${reviewFocusLabel(reviewFocus)} ${dueReviewCount === 1 ? "review" : "reviews"} due`;
}

/** Start's label for the variants that have one: they open the deck on the next card. */
export function learnSummaryStartLabel(variant: LearnSummaryVariant): string | null {
  return variant === "start_reviewing"
    ? "Start Reviewing"
    : variant === "continue_learning"
      ? "Continue Learning"
      : null;
}

/**
 * The summary's resolution without suspending: `undefined` until the local one is known. The
 * content-validated resolution, once known for the current data, is the more precise one: it skips
 * due cards whose content this device can't open.
 */
export function useLearnSummaryResolution({ enabled = true }: { enabled?: boolean } = {}):
  | LearnSummaryPageResolution
  | undefined {
  const { data: localResolution } = useQuery({
    ...useLearnSummaryPageQueryOptions(),
    enabled,
    throwOnError: false,
  });
  const { data: validatedResolution } = useQuery({
    ...useLearnSummaryPageQueryOptions({ validateContent: true }),
    enabled,
    throwOnError: false,
  });
  return validatedResolution ?? localResolution;
}

/**
 * Where the learner stands on the summary: its variant and due count, and Start when the variant
 * has one (`null` handler while a sync from the cloud holds it, with `pendingLabel` saying so).
 */
export function useLearnSummaryStart({
  resolution,
  source,
}: {
  resolution: LearnSummaryPageResolution | undefined;
  /** Where Start was pressed, for `learn_flow_started`. */
  source: "map_home" | "summary";
}) {
  const isPremium = useIsPremium();
  const [reviewFocus] = useDeviceSetting("reviewFocus");
  // Only when this device KNOWS another device changed the account (a sync push arrived) does
  // Start wait for the cloud; every other launch studies the local replica at once.
  const [deviceAccount] = useDeviceAccount();
  const isSyncingFromCloud = useIsSyncingFromCloud(deviceAccount !== null);
  const startLearnDeckFromSummary = useStartLearnDeckFromSummary();
  const learnDeckQueueHeadNavigator = useLearnDeckQueueHeadNavigator();
  const variant =
    resolution === undefined
      ? undefined
      : learnSummaryVariant({ isPremium, resolution, reviewFocus });
  const dueReviewCount =
    resolution === undefined || resolution.isNewUser ? null : resolution.dueReviewCount;
  // Start opens the deck on the next card without naming it: the deck host resolves which card
  // that is and owns its loading. Nothing here is awaited before the route changes.
  const start = useCallback(() => {
    captureAnalyticsEvent("learn_flow_started", {
      cta: variant ?? "unknown",
      due_review_count: dueReviewCount,
      source,
    });
    return startLearnDeckFromSummary(
      () => learnDeckQueueHeadNavigator.replaceImmediately.mutateAsync(),
      { returnToMapHome: source === "map_home" },
    );
  }, [dueReviewCount, learnDeckQueueHeadNavigator, source, startLearnDeckFromSummary, variant]);
  const startLabel = variant === undefined ? null : learnSummaryStartLabel(variant);
  return {
    dueReviewLabel:
      resolution === undefined || resolution.isNewUser
        ? null
        : learnSummaryDueReviewLabel(resolution.dueReviewCount, reviewFocus),
    isPremium,
    pendingLabel: isSyncingFromCloud ? "Syncing from cloud…" : null,
    reviewFocus,
    start: startLabel === null || isSyncingFromCloud ? null : start,
    startLabel,
    variant,
  };
}
